import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Brain, Mail, Loader2, AlertCircle, CheckCircle2, ArrowRight, Lock, User, Eye, EyeOff, ShieldCheck, GraduationCap, Lightbulb } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Turnstile from 'react-turnstile';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const AuthMagicLink = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loginWithGoogle, isAuthenticated, loading: authLoading, setUser } = useAuth();
  
  // State
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'magic'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [userRole, setUserRole] = useState('innovator');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  
  // CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState('');
  
  // Token verification state
  const [verifying, setVerifying] = useState(false);

  // Check for magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !verifying) {
      const redirectTo = localStorage.getItem('redirect_after_auth') || '/dashboard';
      localStorage.removeItem('redirect_after_auth');
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, verifying]);

  const verifyMagicLink = async (token) => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/magic-link/verify?token=${token}`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('if_auth_token', response.data.token);
        }
        
        if (setUser) {
          setUser(response.data.user);
        }
        
        setSuccess('Welcome! Signing you in...');
        
        setTimeout(() => {
          const redirectTo = localStorage.getItem('redirect_after_auth') || '/dashboard';
          localStorage.removeItem('redirect_after_auth');
          navigate(redirectTo, { replace: true });
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link.');
      setVerifying(false);
    }
  };

  // Email/Password Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: email.toLowerCase().trim(),
        password: password
      }, { withCredentials: true });
      
      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('if_auth_token', response.data.token);
        }
        
        if (setUser) {
          setUser(response.data.user);
        }
        
        // Claim any guest sessions after login
        try {
          const guestSessionIds = JSON.parse(localStorage.getItem('guest_session_ids') || '[]');
          await axios.post(`${BACKEND_URL}/api/auth/claim-guest-sessions`, {
            guest_session_ids: guestSessionIds
          }, { withCredentials: true });
          localStorage.removeItem('guest_session_ids');
        } catch (claimErr) {
          // Non-critical - don't show error to user
          console.log('Guest session claim:', claimErr.message);
        }
        
        setSuccess('Welcome back! Signing you in...');
        
        setTimeout(() => {
          const redirectTo = localStorage.getItem('redirect_after_auth') || '/dashboard';
          localStorage.removeItem('redirect_after_auth');
          navigate(redirectTo, { replace: true });
        }, 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Email/Password Signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email || !password || !name) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate CAPTCHA for signup
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        email: email.toLowerCase().trim(),
        password: password,
        name: name.trim(),
        user_role: userRole,
        captcha_token: captchaToken || undefined
      }, { withCredentials: true });
      
      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('if_auth_token', response.data.token);
        }
        
        if (setUser) {
          setUser(response.data.user);
        }
        
        // Claim any guest sessions after signup
        try {
          const guestSessionIds = JSON.parse(localStorage.getItem('guest_session_ids') || '[]');
          await axios.post(`${BACKEND_URL}/api/auth/claim-guest-sessions`, {
            guest_session_ids: guestSessionIds
          }, { withCredentials: true });
          localStorage.removeItem('guest_session_ids');
        } catch (claimErr) {
          // Non-critical - don't show error to user
          console.log('Guest session claim:', claimErr.message);
        }
        
        setSuccess('Account created! Welcome to Idea Fountain!');
        
        setTimeout(() => {
          const redirectTo = localStorage.getItem('redirect_after_auth') || '/dashboard';
          localStorage.removeItem('redirect_after_auth');
          navigate(redirectTo, { replace: true });
        }, 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Signup failed. Please try again.';
      setError(errorMsg);
      // Reset CAPTCHA on error
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    localStorage.setItem('redirect_after_auth', location.state?.from?.pathname || '/dashboard');
    localStorage.setItem('signup_user_role', userRole || 'innovator');
    const redirectUrl = `${window.location.origin}/auth`;
    window.location.href = `https://emergentauthagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle Google OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      handleGoogleCallback(sessionId);
    }
  }, [location.search]);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const result = await loginWithGoogle(sessionId);
      if (result.success) {
        const redirectTo = localStorage.getItem('redirect_after_auth') || '/dashboard';
        localStorage.removeItem('redirect_after_auth');
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.error || 'Google sign-in failed');
      }
    } catch (err) {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Magic Link request
  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/magic-link`, {
        email: email.toLowerCase().trim()
      });
      
      if (response.data.success) {
        setMagicLinkSent(true);
        setSuccess('Magic link sent! Check your email to sign in.');
      } else {
        setError(response.data.error || 'Failed to send magic link');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-slate-600">{verifying ? 'Signing you in...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Brain className="w-10 h-10 text-purple-600" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Idea Fountain
            </h1>
          </div>
          <p className="text-slate-600">Originality Enabling System</p>
        </div>

        <Card className="border-2 border-purple-100 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Welcome, Fountainhead!</CardTitle>
            <CardDescription>
              Sign in to continue your innovation journey
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4 pt-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {/* Success Message */}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* Auth Tabs */}
            <Tabs value={authMode} onValueChange={(v) => { setAuthMode(v); setLoginMethod('password'); setMagicLinkSent(false); }} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login" className="space-y-4 mt-4">
                {/* Login Method Toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('password'); setMagicLinkSent(false); setError(''); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      loginMethod === 'password' 
                        ? 'bg-white shadow text-purple-700' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Lock className="w-4 h-4 inline mr-1" />
                    Password
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoginMethod('magic'); setError(''); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      loginMethod === 'magic' 
                        ? 'bg-white shadow text-purple-700' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Mail className="w-4 h-4 inline mr-1" />
                    Magic Link
                  </button>
                </div>

                {/* Password Login */}
                {loginMethod === 'password' && (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          disabled={loading}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : null}
                      {loading ? 'Signing in...' : 'Sign In'}
                      {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
                    </Button>
                  </form>
                )}

                {/* Magic Link Login */}
                {loginMethod === 'magic' && !magicLinkSent && (
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                      <p className="font-medium">✨ Passwordless Sign In</p>
                      <p className="text-xs mt-1">We&apos;ll email you a magic link for instant access.</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="magic-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="magic-email"
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : null}
                      {loading ? 'Sending...' : 'Send Magic Link'}
                      {!loading && <Mail className="w-5 h-5 ml-2" />}
                    </Button>
                  </form>
                )}

                {/* Magic Link Sent Confirmation */}
                {loginMethod === 'magic' && magicLinkSent && (
                  <div className="text-center space-y-4 py-4">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                      <Mail className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">Check your email!</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        We&apos;ve sent a magic link to <strong>{email}</strong>
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        Click the link in the email to sign in instantly. The link expires in 15 minutes.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setMagicLinkSent(false); setSuccess(''); }}
                      className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
                    >
                      Didn&apos;t receive it? Send again
                    </button>
                  </div>
                )}
              </TabsContent>
              
              {/* Signup Tab */}
              <TabsContent value="signup" className="space-y-4 mt-4">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="John Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Role Selection */}
                  <div className="space-y-2">
                    <Label>I am a</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setUserRole('innovator')}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          userRole === 'innovator' 
                            ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Lightbulb className={`w-5 h-5 ${userRole === 'innovator' ? 'text-violet-600' : 'text-slate-400'}`} />
                        <div>
                          <div className="text-sm font-medium text-slate-800">Innovator</div>
                          <div className="text-xs text-slate-500">Evaluate papers & ideas</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setUserRole('educator')}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                          userRole === 'educator' 
                            ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <GraduationCap className={`w-5 h-5 ${userRole === 'educator' ? 'text-purple-600' : 'text-slate-400'}`} />
                        <div>
                          <div className="text-sm font-medium text-slate-800">Educator</div>
                          <div className="text-xs text-slate-500">Batch evaluate students</div>
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        disabled={loading}
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="signup-confirm"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        disabled={loading}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* CAPTCHA for Signup */}
                  {TURNSTILE_SITE_KEY && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Please verify you&apos;re human</span>
                      </div>
                      <div className="flex justify-center">
                        <Turnstile
                          sitekey={TURNSTILE_SITE_KEY}
                          onSuccess={(token) => setCaptchaToken(token)}
                          onError={() => {
                            setCaptchaToken('');
                            setError('CAPTCHA verification failed. Please try again.');
                          }}
                          onExpire={() => setCaptchaToken('')}
                          theme="light"
                          appearance="interaction-only"
                        />
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6"
                    disabled={loading || (TURNSTILE_SITE_KEY && !captchaToken)}
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : null}
                    {loading ? 'Creating account...' : 'Create Account'}
                    {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">or continue with</span>
              </div>
            </div>

            {/* Google Sign In */}
            <Button
              type="button"
              variant="outline"
              className="w-full py-6 border-2"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {/* Info */}
            <p className="text-center text-xs text-slate-500">
              By signing up, you agree to our Terms of Service and Privacy Policy.
            </p>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-600"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuthMagicLink;
